import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { useBooking } from '../context/BookingContext';
import ServiceCard from '../components/ServiceCard';
import './Services.css';

const Services = () => {
  const { openBooking } = useBooking();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);
  const [apiErrors, setApiErrors] = useState([]);

  useEffect(() => {
    loadServicesData();
  }, []);

  const loadServicesData = async () => {
    try {
      setLoading(true);
      const errors = [];
      const [servicesResponse, pageResponse] = await Promise.allSettled([
        apiService.getServices(),
        apiService.getPage('services')
      ]);

      if (servicesResponse.status === 'fulfilled') {
        const raw = servicesResponse.value.data;
        const servicesData = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
        console.log('[SERVICES] Services from DB:', servicesData.length, servicesData);
        setServices(servicesData);
      } else {
        console.error('[SERVICES] Services API failed:', servicesResponse.reason);
        errors.push('Services: ' + (servicesResponse.reason?.message || 'API unreachable'));
      }

      if (pageResponse.status === 'fulfilled') {
        const raw = pageResponse.value.data;
        const pd = raw?.data || (raw?.success === false ? null : raw) || null;
        console.log('[SERVICES] Page content from DB:', pd);
        setPageData(pd);
      } else {
        console.error('[SERVICES] Page API failed:', pageResponse.reason);
        errors.push('Page content: ' + (pageResponse.reason?.message || 'API unreachable'));
      }

      setApiErrors(errors);
    } catch (err) {
      console.error('[SERVICES] Unexpected error:', err);
      setApiErrors(['Unexpected: ' + err.message]);
    } finally {
      setLoading(false);
    }
  };

  // Fallback static services if database is empty
  const fallbackServices = [
    {
      id: 'yoga',
      service_name: 'Yoga',
      icon: '🧘',
      short_description: 'Yoga is an ancient practice that originated in India thousands of years ago. It is a holistic approach to health and well-being that combines physical postures, breathing techniques, meditation, and ethical principles.',
      full_description: 'The word "yoga" comes from the Sanskrit word "yuj," which means to yoke or unite. This refers to the union of body, mind, and spirit that is the ultimate goal of yoga.',
      benefits: ['Improves flexibility and strength', 'Reduces stress and anxiety', 'Enhances mental clarity', 'Promotes overall well-being']
    },
    {
      id: 'vedic-diet',
      service_name: 'Vedic Diet Counseling',
      icon: '🥗',
      short_description: 'The Vedic diet, also known as the Ayurvedic diet, is a traditional eating system based on the principles of Ayurveda, a holistic health system that originated in India over 5,000 years ago.',
      full_description: 'Our nutritionist helps our clients to develop a healthy and balanced vedic diet plan that meets their individual needs and goals. The diet is designed according to the genetic make up of an individual rather than following a standard chart.',
      benefits: ['Personalized diet plans based on Prakriti', 'Traditional Vedic nutrition principles', 'Holistic approach to health', 'Long-term wellness solutions']
    },
    {
      id: 'meditation',
      service_name: 'Meditation Workshops',
      icon: '🧘‍♀️',
      short_description: 'Experience a sanctuary where peace and serenity flourish, allowing individuals to reconnect with their inner selves by enriching world of meditation and guiding you through various techniques at enhancing mental clarity.',
      full_description: 'We teach breathing techniques and meditation/relaxation techniques to combat stress, lifestyle disorders, and promote inner peace.',
      benefits: ['Guided meditation sessions', 'Stress reduction techniques', 'Mindfulness practices', 'Inner peace and clarity']
    }
  ];

  const displayServices = services.length > 0 ? services : fallbackServices;
  const pageTitle = pageData?.title || pageData?.page_name || "Our Services";
  const pageSubtitle = pageData?.excerpt || "Holistic. Personalised. Conscious.";

  return (
    <div className="services-page">
      {apiErrors.length > 0 && (
        <div style={{background:'#fff3cd', border:'1px solid #ffc107', padding:'12px 20px', fontSize:'13px', color:'#856404'}}>
          <strong>⚠️ Database not connected — showing static content.</strong> API Errors: {apiErrors.join(' | ')}
          {' '}<a href="http://localhost/api/diagnostic.php" target="_blank" rel="noreferrer" style={{color:'#0d6efd'}}>Run Diagnostic</a>
          {' | '}<a href="http://localhost/api/setup_schema.php?confirm=yes" target="_blank" rel="noreferrer" style={{color:'#dc3545'}}>Setup Database</a>
        </div>
      )}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-hero-title">{pageTitle}</h1>
          <p className="page-hero-subtitle">{pageSubtitle}</p>
        </div>
      </section>

      {loading ? (
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      ) : (
        <section className="service-details">
          <div className="container">
            <div className="service-details-grid">
              {displayServices.map((service) => {
                const serviceId = service.id || service.service_code?.toLowerCase().replace(/_/g, '-') || `service-${service.id}`;
                return (
                  <ServiceCard
                    key={service.id || serviceId}
                    service={service}
                    layout="featured"
                    id={serviceId}
                    ctaLabel="View Plans"
                    ctaTo="/plans"
                  />
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="cta-section">
        <div className="container">
          <h2>Begin Your Journey Towards Sampoorna</h2>
          <p>Whatever brings you here, Sampoorna offers a space to pause, explore and reconnect.</p>
          <button type="button" className="btn btn-primary" onClick={() => openBooking({ title: 'Book a Service' })}>
            Book Appointment
          </button>
        </div>
      </section>
    </div>
  );
};

export default Services;
